import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Package, Truck, CheckCircle2 } from "lucide-react";
import { api, formatINR, apiError } from "@/lib/api";

const STEPS = [
  { key: "pending", label: "Order Placed", icon: Clock },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const PAYMENT_LABELS = {
  paid: "Paid",
  advance_paid: "Advance Paid (COD)",
  fully_paid: "Fully Paid",
  pending: "Pending",
  failed: "Failed",
};

const TrackPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [reference, setReference] = useState(searchParams.get("ref") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !reference.trim()) {
      setError("Please enter both your email and order reference");
      return;
    }
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.post("/orders/track", { email, reference });
      setOrder(data);
      window.localStorage.setItem("aarmira_last_order_ref", data.reference);
    } catch (err) {
      setError(apiError(err, "No order found for that email and reference"));
    } finally {
      setLoading(false);
    }
  };

  const activeStep = order ? Math.max(0, STEPS.findIndex((s) => s.key === order.status)) : -1;

  return (
    <main data-testid="track-page" className="min-h-screen bg-cream px-4 py-16 sm:px-8 lg:py-24">
      <div className="grain-overlay" />
      <div className="mx-auto max-w-xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold-muted">
            Aarmira — Fashion and Fortune
          </p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-espresso">Track Your Order</h1>
          <p className="mt-3 text-sm leading-relaxed text-espresso/60">
            Enter the email you ordered with and the reference from your confirmation email.
          </p>
        </motion.div>

        <form
          data-testid="track-form"
          onSubmit={track}
          className="mt-10 space-y-3 border border-gold/25 bg-cream p-6 sm:p-8"
        >
          <input
            data-testid="track-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="w-full border border-espresso/15 bg-transparent px-4 py-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
          />
          <input
            data-testid="track-reference-input"
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            placeholder="Order reference (e.g. DF0FBA7A)"
            maxLength={12}
            className="w-full border border-espresso/15 bg-transparent px-4 py-3 font-mono text-sm uppercase tracking-[0.2em] text-espresso outline-none transition-colors placeholder:normal-case placeholder:tracking-normal placeholder:text-espresso/35 focus:border-gold"
          />
          {error && (
            <p data-testid="track-error" className="text-xs text-red-700">
              {error}
            </p>
          )}
          <button
            data-testid="track-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? "Searching…" : "Find My Order"}
          </button>
        </form>

        {order && (
          <motion.div
            data-testid="track-result"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 border border-gold/25 bg-cream p-6 sm:p-8"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-serif text-2xl text-espresso">
                Order <span data-testid="track-result-reference">#{order.reference}</span>
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-muted">
                {new Date(order.created_at).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
            </div>
            <p className="mt-1 text-sm text-espresso/60">Placed by {order.customer_name}</p>

            <div className="mt-8 flex items-start justify-between">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const reached = i <= activeStep;
                return (
                  <div key={s.key} className="relative flex flex-1 flex-col items-center">
                    {i > 0 && (
                      <span
                        className={`absolute left-0 top-4 h-px w-full -translate-x-1/2 ${
                          reached ? "bg-gold" : "bg-espresso/10"
                        }`}
                      />
                    )}
                    <span
                      data-testid={`track-step-${s.key}`}
                      className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border ${
                        reached
                          ? "border-gold bg-gold text-espresso"
                          : "border-espresso/15 bg-cream text-espresso/30"
                      }`}
                    >
                      <Icon size={14} strokeWidth={1.5} />
                    </span>
                    <span
                      className={`mt-2 text-center font-mono text-[8px] uppercase tracking-[0.15em] sm:text-[9px] ${
                        reached ? "text-espresso" : "text-espresso/35"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 border-t border-espresso/10 pt-5">
              {order.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                  <span className="text-espresso/75">
                    {it.name} <span className="text-gold-muted">× {it.quantity}</span>
                  </span>
                  <span className="price-text text-espresso">{formatINR(it.price * it.quantity)}</span>
                </div>
              ))}
              {order.shipping != null && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-espresso/50">Shipping</span>
                  <span
                    data-testid="track-result-shipping"
                    className={order.shipping === 0 ? "text-gold-muted" : "text-espresso"}
                  >
                    {order.shipping === 0 ? "FREE" : formatINR(order.shipping)}
                  </span>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between border-t border-espresso/10 pt-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold-muted">
                  Total
                </span>
                <span data-testid="track-result-total" className="price-text text-xl text-espresso">
                  {formatINR(order.total)}
                </span>
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em]">
                <span className="text-espresso/40">Payment: </span>
                <span
                  data-testid="track-result-payment"
                  className={
                    ["paid", "advance_paid", "fully_paid"].includes(order.payment_status)
                      ? "text-gold-muted"
                      : "text-espresso/60"
                  }
                >
                  {PAYMENT_LABELS[order.payment_status] || order.payment_status}
                </span>
              </p>
              {order.payment_method === "cod" && order.payment_status === "advance_paid" && (
                <p
                  data-testid="track-cod-balance"
                  className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-espresso/50"
                >
                  {formatINR(order.cod_balance_due || 0)} payable on delivery
                </p>
              )}
              {order.courier_name && (
                <div
                  data-testid="track-courier"
                  className="mt-4 border border-gold/25 bg-cream-dark/60 px-4 py-3"
                >
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold-muted">
                    Courier
                  </p>
                  <p className="mt-1 text-sm text-espresso">
                    {order.courier_name}
                    {order.tracking_number ? ` · Tracking ${order.tracking_number}` : ""}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};

export default TrackPage;
